const API_URL = process.env.REACT_APP_BACKEND_URL;

function getHeaders() {
  const token = localStorage.getItem('toyou_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
}

function getAuthHeaders() {
  const token = localStorage.getItem('toyou_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

async function handleResponse(res) {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Errore di rete' }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

// Models
export async function getModels() {
  const res = await fetch(`${API_URL}/api/models`, { headers: getHeaders() });
  return handleResponse(res);
}

// Conversations
export async function createConversation() {
  const res = await fetch(`${API_URL}/api/conversations`, {
    method: 'POST',
    headers: getHeaders()
  });
  return handleResponse(res);
}

export async function listConversations() {
  const res = await fetch(`${API_URL}/api/conversations`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function getConversation(id) {
  const res = await fetch(`${API_URL}/api/conversations/${id}`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function updateConversation(id, data) {
  const res = await fetch(`${API_URL}/api/conversations/${id}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

export async function deleteConversation(id) {
  const res = await fetch(`${API_URL}/api/conversations/${id}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  return handleResponse(res);
}

// Messages
export async function sendMessage(convId, text, model, file = null) {
  if (file) {
    const formData = new FormData();
    formData.append('text', text);
    formData.append('model', model);
    formData.append('file', file);
    const res = await fetch(`${API_URL}/api/conversations/${convId}/messages`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData
    });
    return handleResponse(res);
  } else {
    const res = await fetch(`${API_URL}/api/conversations/${convId}/messages`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ text, model })
    });
    return handleResponse(res);
  }
}

// Streaming Messages
export async function sendMessageStream(convId, text, model, onChunk, onDone, onError) {
  try {
    const res = await fetch(`${API_URL}/api/conversations/${convId}/messages/stream`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ text, model })
    });
    
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Errore di rete' }));
      throw new Error(err.detail || `HTTP ${res.status}`);
    }
    
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            switch (data.type) {
              case 'user_msg':
                onChunk({ type: 'user_msg', message: data.message });
                break;
              case 'start':
                onChunk({ type: 'start', assistant_msg_id: data.assistant_msg_id });
                break;
              case 'chunk':
                onChunk({ type: 'chunk', content: data.content });
                break;
              case 'title':
                onChunk({ type: 'title', title: data.title });
                break;
              case 'done':
                onDone(data.assistant_message);
                break;
              case 'error':
                onError(data.content);
                break;
              default:
                break;
            }
          } catch (e) {
            // Skip malformed JSON
          }
        }
      }
    }
  } catch (e) {
    onError(e.message);
  }
}

// Image generation
export async function generateImage(convId, prompt) {
  const res = await fetch(`${API_URL}/api/images/generate`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ conversation_id: convId, prompt })
  });
  return handleResponse(res);
}

// Admin
export async function adminGetUsers() {
  const res = await fetch(`${API_URL}/api/admin/users`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function adminCreateUser(data) {
  const res = await fetch(`${API_URL}/api/admin/users`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

export async function adminUpdateUser(userId, data) {
  const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

export async function adminDeleteUser(userId) {
  const res = await fetch(`${API_URL}/api/admin/users/${userId}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  return handleResponse(res);
}

export async function adminResetCredits(userId) {
  const res = await fetch(`${API_URL}/api/admin/users/${userId}/reset-credits`, {
    method: 'POST',
    headers: getHeaders()
  });
  return handleResponse(res);
}

export async function adminGetAnalytics() {
  const res = await fetch(`${API_URL}/api/admin/analytics`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function adminGetAllModels() {
  const res = await fetch(`${API_URL}/api/admin/models`, { headers: getHeaders() });
  return handleResponse(res);
}

// Projects
export async function createProject(data) {
  const res = await fetch(`${API_URL}/api/projects`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

export async function listProjects() {
  const res = await fetch(`${API_URL}/api/projects`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function getProject(id) {
  const res = await fetch(`${API_URL}/api/projects/${id}`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function updateProject(id, data) {
  const res = await fetch(`${API_URL}/api/projects/${id}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

export async function deleteProject(id) {
  const res = await fetch(`${API_URL}/api/projects/${id}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  return handleResponse(res);
}

// Project conversations
export async function listProjectConversations(projectId) {
  const res = await fetch(`${API_URL}/api/projects/${projectId}/conversations`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function createProjectConversation(projectId) {
  const res = await fetch(`${API_URL}/api/projects/${projectId}/conversations`, {
    method: 'POST',
    headers: getHeaders()
  });
  return handleResponse(res);
}

// Knowledge Base
export async function uploadKnowledgeFile(projectId, file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_URL}/api/projects/${projectId}/knowledge-files`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: formData
  });
  return handleResponse(res);
}

export async function listKnowledgeFiles(projectId) {
  const res = await fetch(`${API_URL}/api/projects/${projectId}/knowledge-files`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function deleteKnowledgeFile(projectId, fileId) {
  const res = await fetch(`${API_URL}/api/projects/${projectId}/knowledge-files/${fileId}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  return handleResponse(res);
}

// Artifacts
export async function createArtifact(projectId, data) {
  const res = await fetch(`${API_URL}/api/projects/${projectId}/artifacts`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

export async function listArtifacts(projectId) {
  const res = await fetch(`${API_URL}/api/projects/${projectId}/artifacts`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function getArtifact(projectId, artifactId) {
  const res = await fetch(`${API_URL}/api/projects/${projectId}/artifacts/${artifactId}`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function deleteArtifact(projectId, artifactId) {
  const res = await fetch(`${API_URL}/api/projects/${projectId}/artifacts/${artifactId}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  return handleResponse(res);
}

// App Generation
export async function generateApp(projectId, data) {
  const res = await fetch(`${API_URL}/api/projects/${projectId}/generate-app`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(res);
}

// Export project as ZIP
export function getExportZipUrl(projectId) {
  const token = localStorage.getItem('toyou_token');
  return `${API_URL}/api/projects/${projectId}/export.zip?token=${token}`;
}


// ========== BITBUCKET ==========
export async function getBitbucketSettings() {
  const res = await fetch(`${API_URL}/api/settings/bitbucket`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function saveBitbucketSettings(data) {
  const res = await fetch(`${API_URL}/api/settings/bitbucket`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify(data)
  });
  return handleResponse(res);
}

export async function disconnectBitbucket() {
  const res = await fetch(`${API_URL}/api/settings/bitbucket`, {
    method: 'DELETE', headers: getHeaders()
  });
  return handleResponse(res);
}

export async function bbListRepos(page = 1) {
  const res = await fetch(`${API_URL}/api/bitbucket/repos?page=${page}`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function bbListBranches(repoSlug) {
  const res = await fetch(`${API_URL}/api/bitbucket/repos/${repoSlug}/branches`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function bbCreateBranch(repoSlug, name, fromBranch = 'main') {
  const res = await fetch(`${API_URL}/api/bitbucket/repos/${repoSlug}/branches`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify({ name, from_branch: fromBranch })
  });
  return handleResponse(res);
}

export async function bbImportRepo(repoSlug, projectId, branch = 'main') {
  const res = await fetch(`${API_URL}/api/bitbucket/repos/${repoSlug}/import`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify({ project_id: projectId, branch })
  });
  return handleResponse(res);
}

export async function bbPushToRepo(repoSlug, projectId, branch, message) {
  const res = await fetch(`${API_URL}/api/bitbucket/repos/${repoSlug}/push`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify({ project_id: projectId, branch, message })
  });
  return handleResponse(res);
}

export async function bbCreatePR(repoSlug, title, sourceBranch, destBranch = 'main', description = '') {
  const res = await fetch(`${API_URL}/api/bitbucket/repos/${repoSlug}/pull-requests`, {
    method: 'POST', headers: getHeaders(),
    body: JSON.stringify({ title, source_branch: sourceBranch, dest_branch: destBranch, description })
  });
  return handleResponse(res);
}

export async function bbListPRs(repoSlug) {
  const res = await fetch(`${API_URL}/api/bitbucket/repos/${repoSlug}/pull-requests`, { headers: getHeaders() });
  return handleResponse(res);
}


// Deploy
export async function deployProject(projectId, options = {}) {
  const res = await fetch(`${API_URL}/api/projects/${projectId}/deploy`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify(options)
  });
  return handleResponse(res);
}

export async function getDeployStatus(projectId) {
  const res = await fetch(`${API_URL}/api/projects/${projectId}/deploy/status`, { headers: getHeaders() });
  return handleResponse(res);
}

export async function undeployProject(projectId) {
  const res = await fetch(`${API_URL}/api/projects/${projectId}/deploy`, {
    method: 'DELETE', headers: getHeaders()
  });
  return handleResponse(res);
}

export async function saveDeployConfig(projectId, config) {
  const res = await fetch(`${API_URL}/api/projects/${projectId}/deploy-config`, {
    method: 'POST', headers: getHeaders(), body: JSON.stringify(config)
  });
  return handleResponse(res);
}

export async function adminListDeploys() {
  const res = await fetch(`${API_URL}/api/admin/deploys`, { headers: getHeaders() });
  return handleResponse(res);
}

